'use client';

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface HeroSectionProps {
  onRequestQuote: () => void;
  onScheduleConsultation: () => void;
}

const HeroSection = ({ onRequestQuote, onScheduleConsultation }: HeroSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentMetric, setCurrentMetric] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setCurrentMetric((prev) => (prev + 1) % 3);
    }, 3000);

    return () => clearInterval(interval);
  }, [isHydrated]);

  const metrics = [
  { value: '7+', label: 'Projects Completed', icon: 'CheckCircleIcon' },
  { value: '3', label: 'Countries Served', icon: 'GlobeAltIcon' },
  { value: '15+', label: 'Years Experience', icon: 'StarIcon' }];


  const certifications = [
  { name: 'EPRA', tooltip: 'Energy and Petroleum Regulatory Authority Certified' },
  { name: 'NCA', tooltip: 'National Construction Authority Approved' },
  { name: 'ICT', tooltip: 'Information and Communications Technology Certified' }];


  return (
    <section className="relative min-h-screen flex items-center bg-gradient-to-br from-primary via-secondary to-primary overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <AppImage
          src="https://images.unsplash.com/photo-1632300742963-1f3a929c02d0"
          alt="Modern electrical substation with high voltage transformers and power distribution equipment at sunset"
          className="w-full h-full object-cover"
          priority />

      </div>

      <div className="container mx-auto px-6 py-20 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-accent/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
            <Icon name="BoltIcon" size={20} variant="solid" className="text-accent" />
            <span className="text-sm font-semibold text-white">NEW CAPITAL FOR GENERAL CONTRACTING</span>
          </div>

          <h1 className="font-headline text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            Engineering, Procurement & Contracting Across Africa
          </h1>

          <p className="font-body text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto">
            Delivering world-class electrical and mechanical engineering solutions with direct manufacturer partnerships and proven success in mission-critical projects
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <button
              onClick={onRequestQuote}
              className="bg-accent hover:bg-accent/90 text-white font-cta font-bold px-8 py-4 rounded-lg transition-all duration-250 transform hover:scale-105 shadow-lg flex items-center justify-center gap-2">

              <Icon name="DocumentTextIcon" size={24} variant="solid" />
              Request Project Quote
            </button>
            <button
              onClick={onScheduleConsultation}
              className="bg-white hover:bg-white/90 text-primary font-cta font-bold px-8 py-4 rounded-lg transition-all duration-250 transform hover:scale-105 shadow-lg flex items-center justify-center gap-2">

              <Icon name="ChatBubbleLeftRightIcon" size={24} variant="solid" />
              Schedule Consultation
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {metrics.map((metric, index) =>
            <div
              key={metric.label}
              className={`bg-white/10 backdrop-blur-md rounded-lg p-6 transition-all duration-500 ${
              isHydrated && currentMetric === index ? 'ring-2 ring-accent scale-105' : ''}`
              }>

                <div className="flex items-center justify-center gap-3 mb-2">
                  <Icon name={metric.icon as any} size={32} variant="solid" className="text-accent" />
                  <span className="font-headline text-4xl font-bold text-white">{metric.value}</span>
                </div>
                <p className="font-body text-sm text-white/80">{metric.label}</p>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <span className="text-white/80 font-body text-sm">Certified by:</span>
            {certifications.map((cert) =>
            <div
              key={cert.name}
              className="group relative bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg hover:bg-white/30 transition-all duration-250"
              title={cert.tooltip}>

                <span className="font-cta font-bold text-white text-sm">{cert.name}</span>
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-card text-foreground text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-250 pointer-events-none whitespace-nowrap">
                  {cert.tooltip}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <Icon name="ChevronDownIcon" size={32} variant="outline" className="text-white/60" />
      </div>
    </section>);

};

export default HeroSection;